import { useRef, useState } from 'react';

/**
 * Cadastro do ganhador.
 *
 * A foto é recortada em quadrado de 420px **no cliente**, como no protótipo:
 * foto de celular moderno tem 4 MB e o salão costuma estar numa rede fraca —
 * subir o original atrasaria a comemoração.
 */

const PHOTO_SIZE = 420;

export function WinnerDialog({
  cardNumber,
  busy,
  onCancel,
  onConfirm,
}: {
  cardNumber: string | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (input: { name: string; cardNumber: string | null; photo: Blob | null }) => void;
}) {
  const [name, setName] = useState('');
  const [card, setCard] = useState(cardNumber ?? '');
  const [preview, setPreview] = useState<string | null>(null);
  const [photo, setPhoto] = useState<Blob | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const pickPhoto = async (file: File) => {
    const url = URL.createObjectURL(file);

    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = reject;
        element.src = url;
      });

      const canvas = document.createElement('canvas');
      canvas.width = PHOTO_SIZE;
      canvas.height = PHOTO_SIZE;

      const side = Math.min(image.width, image.height);
      canvas
        .getContext('2d')
        ?.drawImage(
          image,
          (image.width - side) / 2,
          (image.height - side) / 2,
          side,
          side,
          0,
          0,
          PHOTO_SIZE,
          PHOTO_SIZE,
        );

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.82),
      );

      if (blob) {
        setPhoto(blob);
        setPreview(canvas.toDataURL('image/jpeg', 0.82));
      }
    } catch {
      // Formato que o navegador não abre: segue sem foto, com as iniciais.
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="overlay">
      <form
        className="modal"
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) return;
          onConfirm({ name: name.trim(), cardNumber: card.trim() || null, photo });
        }}
      >
        <h2>Temos um ganhador?</h2>

        <div className="wgrid">
          <label className="photo" onClick={() => fileRef.current?.click()}>
            {preview ? (
              <img src={preview} alt="" />
            ) : (
              <span>
                Enviar foto
                <br />
                <small style={{ fontWeight: 400 }}>opcional</small>
              </span>
            )}
          </label>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void pickPhoto(file);
            }}
          />

          <div className="fields" style={{ display: 'grid', gap: 12 }}>
            <label>
              Nome do ganhador
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                autoComplete="off"
                autoFocus
              />
            </label>
            <label>
              Nº da cartela
              <input
                value={card}
                onChange={(event) => setCard(event.target.value)}
                inputMode="numeric"
                autoComplete="off"
              />
            </label>
          </div>
        </div>

        <div className="actions">
          <button type="button" className="btn" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
            Confirmar e comemorar
          </button>
        </div>
      </form>
    </div>
  );
}
